import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
    AlertBox,
    Box,
    Button,
    FlexBox,
    FormField,
    FormInputGroup,
    FormLabel,
    Icon,
    IconButton,
    SelectionSet,
    SelectionSetInput,
    SelectionSetLabel,
    Typography,
} from '@vp/swan';
import { TimezoneSelector, bugtracker } from '@99designs/design-services-common';
import {
    DateSelector,
    TimeSelector,
    isAppointmentValid,
    useConfirmDesignLiveTimeIsAvailable,
    useDesignLiveAppointmentUpdatedMutation,
} from '@99designs/feature-design-services-brief';
import { __ } from '@99designs/i18n';
import { usePage } from '@99designs/tracking';
import { useChatActions } from '../contexts/ChatContext';
import { REVIEW_METHOD } from '../types/selectors';
import { HelpMeAnswerButton } from './HelpMeAnswerButton';
import type { ReviewMethod, ReviewMethodOption, ReviewMethodSelection } from '../types/selectors';

export type { ReviewMethodOption, ReviewMethodSelection };

export interface ReviewMethodSelectorProps {
    options: ReviewMethodOption[];
    onComplete: (selection: ReviewMethodSelection) => void | Promise<boolean>;
    defaultValue?: ReviewMethod;
    preselectedDate?: string;
    preselectedTime?: string;
    preselectedTimezone?: string;
}

const designLiveUnavailableErrorMessage = __(
    'Your selected DesignLive session is no longer available. Please select a new day and time.',
    {
        _context:
            'Validation error message when the selected appointment time is no longer available',
    }
);

const designLiveCheckErrorMessage = __(
    'There was an error checking the availability of your selected DesignLive session. Please try again.',
    {
        _context:
            'Validation error message when there is an error checking appointment time availability',
    }
);
export function ReviewMethodSelector({
    options,
    onComplete,
    defaultValue,
    preselectedDate,
    preselectedTime,
    preselectedTimezone,
}: ReviewMethodSelectorProps) {
    const timeFieldId = useId();
    const { locale } = usePage();
    const { requestId } = useChatActions();

    const browserTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

    const [currentStep, setCurrentStep] = useState(1);
    const [selectedMethod, setSelectedMethod] = useState<ReviewMethod | null>(defaultValue ?? null);
    const [date, setDateDispatcher] = useState<string | null>(preselectedDate ?? null);
    const [time, setTime] = useState<string>(preselectedTime ?? '');
    const [timezone, setTimezone] = useState(preselectedTimezone ?? browserTimezone);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [isValidating, setIsValidating] = useState(false);

    const { checkAvailability } = useConfirmDesignLiveTimeIsAvailable(date, time, timezone);

    const [designLiveAppointmentUpdated] = useDesignLiveAppointmentUpdatedMutation();

    const handleTimezoneChange = (newTimezone: string) => {
        setTimezone(newTimezone);
        setDateDispatcher(null);
        setTime('');
    };

    const setDate = (newDate: string | null) => {
        const isSameDay = date && newDate && date === newDate;
        if (!isSameDay) {
            setTime('');
        }
        setDateDispatcher(newDate);
    };

    useEffect(() => {
        setValidationError(null);
    }, [date, time, timezone]);

    const totalSteps = selectedMethod === REVIEW_METHOD.DesignLive ? 2 : 1;

    const stepHeadingRef = useRef<HTMLHeadingElement>(null);
    const [prevStep, setPrevStep] = useState(1);
    useEffect(() => {
        requestAnimationFrame(() => {
            stepHeadingRef.current?.focus();
        });
    }, []);
    useEffect(() => {
        if (currentStep !== prevStep) {
            setPrevStep(currentStep);
            requestAnimationFrame(() => {
                stepHeadingRef.current?.focus();
            });
        }
    }, [currentStep, prevStep]);

    const handleMethodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as ReviewMethod;
        setSelectedMethod(value);
        if (value === REVIEW_METHOD.Standard) {
            setCurrentStep(1);
        }
    };

    const handleNext = () => {
        if (!selectedMethod) return;
        if (selectedMethod === REVIEW_METHOD.Standard) {
            onComplete({ method: REVIEW_METHOD.Standard });
        } else {
            setCurrentStep(2);
        }
    };

    const handleContinue = async () => {
        if (!date || !time) return;

        setIsValidating(true);
        try {
            if (!isAppointmentValid(time)) {
                setValidationError(designLiveUnavailableErrorMessage);
                return;
            }

            const isAvailable = await checkAvailability();
            if (isAvailable === false) {
                setValidationError(designLiveUnavailableErrorMessage);
                return;
            }
            if (isAvailable === null) {
                setValidationError(designLiveCheckErrorMessage);
                return;
            }

            const chatSucceeded = await onComplete({
                method: REVIEW_METHOD.DesignLive,
                scheduledDate: date,
                scheduledTime: time,
                timezone,
            });

            if (!chatSucceeded) {
                return;
            }

            if (requestId) {
                try {
                    const { errors } = await designLiveAppointmentUpdated({
                        variables: {
                            requestId,
                            input: { appointmentTime: time, timezone },
                        },
                    });

                    if (errors && errors.length > 0) {
                        throw new Error(errors.map((e) => e.message).join('; '));
                    }
                } catch (e) {
                    const err = e instanceof Error ? e : new Error(String(e));
                    bugtracker.notify(err, (event) => {
                        event.context = `Error firing DesignLiveAppointmentUpdated for requestId: ${requestId}`;
                        event.addMetadata('appointment', { requestId, time, timezone });
                    });
                }
            }
        } finally {
            setIsValidating(false);
        }
    };

    const canContinue = Boolean(date && time) && !isValidating;

    return (
        <Box
            style={{
                backgroundColor: 'var(--swan-sem-color-bg-active)',
                border: '1px solid var(--swan-sem-color-border-standard)',
                padding: 'var(--swan-sem-space-5)',
                borderRadius:
                    'var(--swan-sem-border-radius-strong) var(--swan-sem-border-radius-strong) 0 0',
            }}
        >
            {/* Header: Title + Pagination */}
            <FlexBox
                justifyContent="space-between"
                alignItems="center"
                style={{
                    minHeight: '44px',
                    marginBottom: 'var(--swan-sem-space-5)',
                }}
            >
                <Typography
                    ref={stepHeadingRef}
                    tabIndex={-1}
                    fontSkin="title-item"
                    style={{ outline: 'none', boxShadow: 'none' }}
                >
                    {currentStep === 1
                        ? 'How would you like to work with your designer?'
                        : 'Choose a date and time'}
                </Typography>
                <FlexBox
                    alignItems="center"
                    gap={2}
                    style={{
                        visibility: totalSteps > 1 ? 'visible' : 'hidden',
                    }}
                >
                    <IconButton
                        skin="tertiary"
                        onClick={() => setCurrentStep(1)}
                        disabled={currentStep === 1}
                        aria-label="Previous step"
                        tooltip="Previous step"
                    >
                        <Icon iconType="chevronLeft" />
                    </IconButton>
                    <Typography fontSkin="body-small-bold" display={{ xs: 'none', sm: 'block' }}>
                        {currentStep} of {totalSteps}
                    </Typography>
                    <IconButton
                        skin="tertiary"
                        onClick={() => setCurrentStep(2)}
                        disabled={currentStep === 2}
                        aria-label="Next step"
                        tooltip="Next step"
                    >
                        <Icon iconType="chevronRight" />
                    </IconButton>
                </FlexBox>
            </FlexBox>

            {/* Step 1: Method Selection */}
            {currentStep === 1 && (
                <SelectionSet
                    variant="single-select"
                    selectedValue={selectedMethod ?? undefined}
                    onChange={handleMethodChange}
                    aria-label="How would you like to work with your designer?"
                    style={{ gap: 'var(--swan-sem-space-3)' }}
                >
                    {options.map((option) => (
                        <SelectionSetInput key={option.value} value={option.value}>
                            <SelectionSetLabel
                                style={{
                                    padding: 0,
                                    ...(selectedMethod !== option.value && {
                                        backgroundColor: 'var(--swan-sem-color-bg-standard)',
                                    }),
                                }}
                            >
                                <FlexBox
                                    as="span"
                                    alignItems="center"
                                    gap={3}
                                    style={{
                                        padding: 'var(--swan-sem-space-6) var(--swan-sem-space-5)',
                                    }}
                                >
                                    <Icon
                                        iconType={
                                            option.value === REVIEW_METHOD.Standard
                                                ? 'mail'
                                                : 'videoChat'
                                        }
                                        size="standard"
                                    />
                                    <Typography fontSkin="body-standard-bold" as="span">
                                        {option.name}
                                    </Typography>
                                </FlexBox>
                            </SelectionSetLabel>
                        </SelectionSetInput>
                    ))}
                </SelectionSet>
            )}

            {/* Step 2: Scheduling */}
            {currentStep === 2 && (
                <Box>
                    {/* Timezone Selector */}
                    <FormField mb={4}>
                        <FormLabel fontSkin="body-standard-bold" marginBottom={'3'}>
                            Your timezone
                        </FormLabel>
                        <FormInputGroup>
                            <TimezoneSelector
                                value={timezone}
                                onChange={handleTimezoneChange}
                                browserTimezone={browserTimezone}
                                locale={locale}
                            />
                        </FormInputGroup>
                    </FormField>

                    {/* Date Picker */}
                    <FormField mb={4}>
                        <FormLabel fontSkin="body-standard-bold" marginBottom={'3'}>
                            Select a date
                        </FormLabel>
                        <DateSelector
                            timezone={timezone}
                            date={date}
                            setDate={setDate}
                            immutable={false}
                        />
                    </FormField>

                    {/* Time Picker */}
                    {date && isDateFormattedCorrectly(date) && (
                        <FormField mb={4}>
                            <FormLabel fontSkin="body-standard-bold" htmlFor={timeFieldId}>
                                Select a time
                            </FormLabel>
                            <TimeSelector
                                timezone={timezone}
                                date={date}
                                time={time}
                                setTime={setTime}
                                id={timeFieldId}
                                immutable={false}
                            />
                        </FormField>
                    )}

                    {validationError && (
                        <AlertBox skin="error" style={{ width: '100%' }}>
                            <Typography fontSkin="body-standard" fontSize="small">
                                {validationError}
                            </Typography>
                        </AlertBox>
                    )}
                </Box>
            )}

            {/* Footer: Actions */}
            <FlexBox
                justifyContent={currentStep === 1 ? 'space-between' : 'flex-end'}
                alignItems="center"
                gap={3}
                style={{
                    marginTop: 'var(--swan-sem-space-5)',
                }}
            >
                {currentStep === 1 && (
                    <HelpMeAnswerButton
                        selectorType="review-method"
                        options={options.map(({ name }) => ({ name }))}
                    />
                )}
                {currentStep === 1 ? (
                    <Button skin="secondary" onClick={handleNext} disabled={!selectedMethod}>
                        {selectedMethod === REVIEW_METHOD.DesignLive ? 'Next' : 'Continue'}
                    </Button>
                ) : (
                    <Button skin="secondary" onClick={handleContinue} disabled={!canContinue}>
                        Continue
                    </Button>
                )}
            </FlexBox>
        </Box>
    );
}

// Check if the date is in the format YYYY-MM-DD
function isDateFormattedCorrectly(date: string) {
    return /^\d{4}-\d{2}-\d{2}$/.test(date);
}
